/**
 * PortfolioSummary Component
 * 
 * Holdings overview
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './PortfolioSummary.css'

interface Holding {
  symbol: string
  name: string
  quantity: number
  avgPrice: number
  currentPrice: number
  pnl: number
  pnlPercent: number
}

interface PortfolioSummaryProps {
  holdings?: Holding[]
  totalValue?: number
  totalPnL?: number
  totalPnLPercent?: number
  onHoldingClick?: (holding: Holding) => void
  className?: string
}

// Mock data
const mockHoldings: Holding[] = [
  { symbol: 'RELIANCE', name: 'Reliance Industries', quantity: 100, avgPrice: 2500, currentPrice: 2580, pnl: 8000, pnlPercent: 3.2 },
  { symbol: 'HDFCBANK', name: 'HDFC Bank', quantity: 50, avgPrice: 1650, currentPrice: 1680, pnl: 1500, pnlPercent: 1.82 },
  { symbol: 'INFY', name: 'Infosys', quantity: 200, avgPrice: 1450, currentPrice: 1520, pnl: 14000, pnlPercent: 4.83 },
  { symbol: 'TCS', name: 'Tata Consultancy', quantity: 25, avgPrice: 3800, currentPrice: 3750, pnl: -1250, pnlPercent: -1.32 },
]

const PortfolioSummary: React.FC<PortfolioSummaryProps> = ({
  holdings = mockHoldings,
  totalValue = 284500,
  totalPnL = 22250,
  totalPnLPercent = 8.48,
  onHoldingClick,
  className = ''
}) => {
  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  const isProfit = totalPnL >= 0

  return (
    <div className={`portfolio-summary ${className}`}>
      {/* Summary Header */}
      <div className="portfolio-header">
        <div className="portfolio-title">
          <h3>💼 Portfolio</h3>
          <span className="holding-count">{holdings.length} Holdings</span>
        </div>
        <div className="portfolio-value">
          <span className="value-label">Total Value</span>
          <span className="value-amount">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      {/* P&L Banner */}
      <div className={`pnl-banner ${isProfit ? 'profit' : 'loss'}`}>
        <div className="pnl-icon">{isProfit ? '📈' : '📉'}</div>
        <div className="pnl-info">
          <span className="pnl-label">Today's P&L</span>
          <span className="pnl-value">
            {isProfit ? '+' : ''}{formatCurrency(totalPnL)} ({isProfit ? '+' : ''}{totalPnLPercent.toFixed(2)}%)
          </span>
        </div>
      </div>

      {/* Holdings List */}
      <div className="holdings-list">
        {holdings.map((holding) => {
          const holdingPnL = holding.pnl >= 0
          return (
            <div 
              key={holding.symbol}
              className="holding-item"
              onClick={() => onHoldingClick?.(holding)}
            >
              <div className="holding-info">
                <div className="holding-symbol-row">
                  <span className="holding-symbol">{holding.symbol}</span>
                  <span className="holding-quantity">×{holding.quantity}</span>
                </div>
                <span className="holding-name">{holding.name}</span>
              </div>
              
              <div className="holding-prices">
                <span className="current-price">
                  {formatCurrency(holding.currentPrice * holding.quantity)}
                </span>
                <span className={`holding-pnl ${holdingPnL ? 'profit' : 'loss'}`}>
                  {holdingPnL ? '+' : ''}{formatCurrency(holding.pnl)} ({holdingPnL ? '+' : ''}{holding.pnlPercent.toFixed(2)}%)
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="portfolio-footer">
        <button className="view-all-btn">
          View All Holdings →
        </button>
      </div>
    </div>
  )
}

export default PortfolioSummary